import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { scheduleContract } from "@common/contracts/schedule.contract";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    ScheduleBillingFailure,
    scheduleMarkBilledBulkErrorSchema,
} from "@common/schemas/domains/accounting/scheduleBilling.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import {
    SUPPRESS_ERROR_TOAST_HEADERS,
    invalidateAfterLifecycleAction,
    requestScheduleAction,
} from "./scheduleTemplate.api";

const scheduleClient = getBackendAPIClient(scheduleContract);

/** Bulk mark billed refused: every schedule that can't be billed, nothing written. */
export class ScheduleBillingBulkFailureError extends Error {
    constructor(readonly failures: ScheduleBillingFailure[]) {
        super(failures[0]?.message ?? "Failed to mark schedules as billed");
        this.name = "ScheduleBillingBulkFailureError";
    }
}

function toBulkFailureError(error: AxiosError): Error | null {
    const bulkError = scheduleMarkBilledBulkErrorSchema.safeParse(
        error.response?.data,
    );
    return bulkError.success
        ? new ScheduleBillingBulkFailureError(bulkError.data.failures)
        : null;
}

/**
 * The client's default toast is suppressed: the caller toasts the server's
 * own message (lock, cumulative rule, eligibility) once. `pick` narrows the
 * success response at the call site, where ts-rest still knows the status
 * union; anything else is the fallback failure.
 */
async function requestBillingAction<Response, Value>(
    request: () => Promise<Response>,
    pick: (response: Response) => Value | null,
    fallbackMessage: string,
): Promise<Value> {
    const response = await requestScheduleAction(
        request,
        fallbackMessage,
        toBulkFailureError,
    );
    const value = pick(response);
    if (value === null) {
        throw new Error(fallbackMessage);
    }
    return value;
}

export function useMarkScheduleBilledMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: (options: {
            applicationId: string;
            entityId: number;
            billedDate: string;
            amount: number;
        }): Promise<ScheduleApplicationListItem> =>
            requestBillingAction(
                () =>
                    scheduleClient.markApplicationBilled({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.applicationId,
                        },
                        body: {
                            entityId: options.entityId,
                            billedDate: options.billedDate,
                            amount: options.amount,
                        },
                    }),
                (response) =>
                    response.status === 200 ? response.body.application : null,
                "Failed to mark the schedule as billed",
            ),
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
    });
}

export function useMarkScheduleUnbilledMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: (options: {
            applicationId: string;
            entityId: number;
        }): Promise<ScheduleApplicationListItem> =>
            requestBillingAction(
                () =>
                    scheduleClient.markApplicationUnbilled({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: {
                            workspaceId: workspaceId ?? "",
                            id: options.applicationId,
                        },
                        body: { entityId: options.entityId },
                    }),
                (response) =>
                    response.status === 200 ? response.body.application : null,
                "Failed to mark the schedule as unbilled",
            ),
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
    });
}

export function useMarkSchedulesBilledBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: (options: {
            applicationIds: string[];
            entityId: number;
            billedDate: string;
        }): Promise<ScheduleApplicationListItem[]> =>
            requestBillingAction(
                () =>
                    scheduleClient.markApplicationsBilledBulk({
                        extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                        params: { workspaceId: workspaceId ?? "" },
                        body: {
                            entityId: options.entityId,
                            applicationIds: options.applicationIds,
                            billedDate: options.billedDate,
                        },
                    }),
                (response) =>
                    response.status === 200 ? response.body.applications : null,
                "Failed to mark schedules as billed",
            ),
        onSuccess: () => invalidateAfterLifecycleAction(workspaceId),
    });
}
